import {
  ConsentPreferences,
  ConsentProof,
  CONSENT_COOKIE_NAME,
  CONSENT_EXPIRY_DAYS,
  CONSENT_PROOF_KEY,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
} from './types'

/** Set a first-party cookie */
function setCookie(name: string, value: string, days: number): void {
  const date = new Date()

  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;SameSite=Lax;Secure`
}

/** Get a cookie by name */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))

  return match ? decodeURIComponent(match[1]) : null
}

/** Delete a cookie */
function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax;Secure`
}

/**
 * Save consent preferences to both cookie and localStorage (fallback).
 */
export function saveConsent(preferences: ConsentPreferences): void {
  const data = JSON.stringify(preferences)

  // Primary: first-party cookie (12 months)
  setCookie(CONSENT_COOKIE_NAME, data, CONSENT_EXPIRY_DAYS)

  // Fallback: localStorage
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, data)
  } catch {
    // localStorage may be unavailable in private browsing
  }

  // Store consent proof for audit
  saveConsentProof(preferences)
}

/**
 * Load consent preferences from cookie or localStorage fallback.
 * Returns null if no consent has been given yet.
 */
export function loadConsent(): ConsentPreferences | null {
  // Try cookie first
  const cookieValue = getCookie(CONSENT_COOKIE_NAME)
  if (cookieValue) {
    try {
      return JSON.parse(cookieValue) as ConsentPreferences
    } catch {
      // Invalid cookie data — clear it
      deleteCookie(CONSENT_COOKIE_NAME)
    }
  }

  // Fallback: localStorage
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)

    if (stored) {
      const preferences = JSON.parse(stored) as ConsentPreferences

      // Re-sync to cookie if localStorage had it but cookie didn't
      setCookie(CONSENT_COOKIE_NAME, stored, CONSENT_EXPIRY_DAYS)

      return preferences
    }
  } catch {
    // localStorage unavailable
  }

  return null
}

/**
 * Clear all consent data (for testing or when user requests data deletion).
 */
export function clearConsent(): void {
  deleteCookie(CONSENT_COOKIE_NAME)
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY)
    localStorage.removeItem(CONSENT_PROOF_KEY)
  } catch {
    // ignore
  }
}

/**
 * Save a proof-of-consent record for GDPR audit compliance.
 * Stores timestamp, consent version, categories chosen, and anonymized user agent.
 * Appends to an array in localStorage (kept for 3+ years).
 */
function saveConsentProof(preferences: ConsentPreferences): void {
  const proof: ConsentProof = {
    timestamp: new Date().toISOString(),
    consentVersion: CONSENT_VERSION,
    categories: { ...preferences },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.substring(0, 100) : 'unknown',
  }

  try {
    const existing = localStorage.getItem(CONSENT_PROOF_KEY)
    const proofs: ConsentProof[] = existing ? JSON.parse(existing) : []

    proofs.push(proof)

    // Keep last 50 entries to avoid unbounded growth
    const trimmed = proofs.slice(-50)

    localStorage.setItem(CONSENT_PROOF_KEY, JSON.stringify(trimmed))
  } catch {
    // localStorage unavailable — consent proof cannot be stored client-side
    // In production, you'd POST this to your backend
  }
}
